import { type Patient, isRemovedPatient } from '@assist/core';
import { ValidationError } from '@assist/offline';
import { useEffect, useState } from 'preact/hooks';
import { EthDatePicker } from '../components/EthDatePicker.js';
import { Field } from '../components/Field.js';
import { app, clock } from '../controller.js';
import { go } from '../hooks.js';
import { t } from '../i18n.js';
import {
  type FormErrors,
  type PatientForm,
  displayName,
  emptyForm,
  fromPatient,
  mrnOf,
  toPatient,
  validate,
} from '../patient.js';
import { PatientChrome } from '../shell.js';

export function PatientEdit({ id }: { id?: string | undefined }) {
  const [existing, setExisting] = useState<Patient | null>(null);
  const [f, setF] = useState<PatientForm>(emptyForm());
  const [errors, setErrors] = useState<FormErrors>({});
  const [dupes, setDupes] = useState<Patient[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const today = clock.today();
  const todayEth = clock.todayEth();

  useEffect(() => {
    if (!id) return;
    void app.session?.get<Patient>('Patient', id).then((p) => {
      if (p) {
        setExisting(p);
        setF(fromPatient(p, today));
      }
    });
  }, [id, today]);

  const set = <K extends keyof PatientForm>(k: K, v: PatientForm[K]) => {
    setF({ ...f, [k]: v });
    setDupes(null);
    if (errors[k]) {
      const { [k]: _fixed, ...rest } = errors;
      setErrors(rest);
    }
  };

  const submit = async (e: Event, force = false) => {
    e.preventDefault();
    const v = validate(f, t);
    setErrors(v);
    if (Object.keys(v).length) return;
    const s = app.session;
    if (!s) return;
    if (!force && f.mrn.trim()) {
      const same = (await s.searchPatients(f.mrn.trim(), 10)).filter(
        (p) => mrnOf(p) === f.mrn.trim() && p.id !== existing?.id,
      );
      if (same.length) {
        setDupes(same);
        return;
      }
    }
    setBusy(true);
    setErr(null);
    try {
      const saved = await s.save(toPatient(f, today, existing ?? undefined));
      app.saved();
      go(`patient/${saved.id}`);
    } catch (x) {
      setErr(x instanceof ValidationError ? x.message : x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
    }
  };

  const input = (
    k: 'first' | 'father' | 'grandfather' | 'phone' | 'mrn' | 'kebele' | 'woreda' | 'age',
    extra: Record<string, unknown> = {},
  ) => <input id={k} value={f[k]} onInput={(e) => set(k, e.currentTarget.value)} {...extra} />;

  return (
    <>
      {id ? <PatientChrome id={id} here="edit" /> : null}
      <form class="card" onSubmit={(e) => submit(e)} noValidate>
        <h3>{existing ? `${t.edit}: ${displayName(existing)}` : t.registerPatient}</h3>
        <div class="grid3">
          <Field label={`${t.firstName} *`} for="first" error={errors.first}>
            {input('first', { autoComplete: 'off' })}
          </Field>
          <Field label={`${t.fatherName} *`} for="father" error={errors.father}>
            {input('father', { autoComplete: 'off' })}
          </Field>
          <Field label={t.grandfatherName} for="grandfather">
            {input('grandfather', { autoComplete: 'off' })}
          </Field>
        </div>
        <Field label={`${t.sex} *`} error={errors.gender}>
          <div class="seg">
            {(['female', 'male'] as const).map((g) => (
              <label key={g} class={f.gender === g ? 'on' : ''}>
                <input
                  type="radio"
                  name="gender"
                  value={g}
                  checked={f.gender === g}
                  onChange={() => set('gender', g)}
                />
                {g === 'female' ? t.female : t.male}
              </label>
            ))}
          </div>
        </Field>
        {f.dobMode === 'date' ? (
          <Field label={`${t.birthDate} *`} for="dob" error={errors.dob}>
            <EthDatePicker
              id="dob"
              value={f.dob}
              todayEth={todayEth}
              onChange={(v) => set('dob', v)}
            />
            <button
              type="button"
              class="link"
              onClick={() => setF({ ...f, dobMode: 'age', dob: null })}
            >
              {t.ageInstead}
            </button>
          </Field>
        ) : (
          <Field label={`${t.ageYears} (${t.estimated}) *`} for="age" error={errors.age}>
            {input('age', { inputMode: 'numeric', maxLength: 3 })}
            <button
              type="button"
              class="link"
              onClick={() => setF({ ...f, dobMode: 'date', age: '' })}
            >
              {t.dateInstead}
            </button>
          </Field>
        )}
        <div class="grid2">
          <Field label={t.mrn} for="mrn">
            {input('mrn', { autoComplete: 'off' })}
          </Field>
          <Field label={t.phone} for="phone" error={errors.phone}>
            {input('phone', { inputMode: 'tel', autoComplete: 'off' })}
          </Field>
          {f.phone.trim() ? (
            <label class="check">
              <input
                type="checkbox"
                checked={f.smsConsent}
                onChange={(e) => set('smsConsent', e.currentTarget.checked)}
              />{' '}
              {t.smsConsent}
            </label>
          ) : null}
          <Field label={t.kebele} for="kebele">
            {input('kebele')}
          </Field>
          <Field label={t.woreda} for="woreda">
            {input('woreda')}
          </Field>
        </div>
        {dupes ? (
          <div class="alert">
            <p>{t.possibleDuplicate}</p>
            <ul>
              {dupes.map((d) => (
                <li key={d.id}>
                  <a href={`#/patient/${d.id}`}>{displayName(d)}</a>
                  {isRemovedPatient(d) ? <span class="pill amber">{t.removedPill}</span> : null}
                </li>
              ))}
            </ul>
            <button type="button" onClick={(e) => submit(e, true)}>
              {t.saveAnyway}
            </button>
          </div>
        ) : null}
        {err ? <p class="alert">{err}</p> : null}
        <div class="actions sticky-actions">
          <button type="button" class="ghost" onClick={() => history.back()}>
            {t.cancel}
          </button>
          <button type="submit" class="primary" disabled={busy}>
            {busy ? t.saving : t.save}
          </button>
        </div>
      </form>
    </>
  );
}
